import { evidenceObject } from './journeyHistory';
import type { JourneyEvent, JourneyPage, JourneyVersion } from './journeyHistory';

export function historyHeading(page?: JourneyPage, state?: 'loading' | 'denied' | 'error'): string {
  const prefix = 'Prospect history · ';
  if (state !== undefined)
    return (
      prefix + { loading: 'Loading', denied: 'Access restricted', error: 'Unavailable' }[state]
    );
  if (!page || page.prospects.length === 0) return prefix + 'No recorded origin';
  if (page.prospects.length > 1) return `${prefix}${page.prospects.length} prospect records`;
  return (
    prefix +
    (page.prospect_dm_count === 0
      ? 'No DMs recorded'
      : `${page.prospect_dm_count} ${page.prospect_dm_count === 1 ? 'DM' : 'DMs'} as prospect`)
  );
}
export function historyEvents(pages: readonly JourneyPage[]): JourneyEvent[] {
  const seen = new Map<number, JourneyEvent>();
  for (const page of pages) for (const event of page.events) seen.set(event.id, event);
  return [...seen.values()].sort((a, b) => a.id - b.id);
}
export function eventProspect(event: JourneyEvent, page: JourneyPage): string | null {
  if (event.table_name === 'prospects')
    return typeof event.source_key.id === 'string' ? event.source_key.id : null;
  if (typeof event.facts.prospect_id === 'string') return event.facts.prospect_id;
  if (event.table_name === 'lead_activity') {
    const origin = page.activity_origins.find((o) => o.activity_id === event.source_key.id);
    return origin?.origin_count === 1 ? origin.prospect_id : null;
  }
  return null;
}
export function historyEventTitle(event: JourneyEvent): string {
  const reason = typeof event.detail.reason === 'string' ? ` · Reason: ${event.detail.reason}` : '';
  if (event.operation === 'activity_withdrawn') return `DM entry withdrawn${reason}`;
  if (event.operation === 'deleted') return 'Source record deleted · Removal intent not recorded';
  if (event.operation === 'message_corrected') return `Entry corrected${reason}`;
  if (event.operation.startsWith('message_')) return `Message selection recorded${reason}`;
  if (event.operation === 'baseline')
    return 'Baseline at migration time · Earlier changes may be unavailable.';
  return `${event.label} · ${event.operation.replaceAll('_', ' ')}${reason}`;
}
export function selectedVersion(
  event: JourneyEvent,
  versions: readonly JourneyVersion[],
  previous = false,
) {
  const selected = previous
    ? evidenceObject(event.detail.previous)
      ? event.detail.previous
      : {}
    : evidenceObject(event.detail.new)
      ? event.detail.new
      : evidenceObject(event.message_selection)
        ? event.message_selection
        : event.facts;
  const versionId = selected.version_id ?? selected.message_version;
  const slot = selected.slot ?? selected.message_slot;
  const version = versions.find((v) => v.id === versionId);
  if (version !== undefined && typeof slot === 'number' && slot >= 1 && slot <= 8) {
    const name = version.fields.name;
    const body = version.fields[`e${slot}_body`];
    return {
      label: `${previous ? 'Previously selected' : 'Selected'} library wording · ${typeof name === 'string' ? name : 'Name not recorded'} · Slot ${slot} · Version ${version.id}`,
      body: typeof body === 'string' ? body : '',
      draft: selected.review_state === 'draft',
      reviewStateKnown: selected.review_state === 'draft' || selected.review_state === 'library',
      oneOff: selected.is_one_off === true,
      baseline: version.observation === 'baseline',
    };
  }
  return null;
}

export function historyEventDates(event: JourneyEvent): string[] {
  const occurred = typeof event.facts.occurred_at === 'string' ? event.facts.occurred_at : null;
  return [
    ...(occurred === null
      ? []
      : [
          event.facts.occurred_is_day === true
            ? `${occurred.slice(0, 10)} · Recorded day; exact time not recorded`
            : `${occurred} · Recorded occurrence`,
        ]),
    `${event.recorded_at} · Evidence captured`,
  ];
}
